import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * CardGrid — the generic card-grid engine for @aisquare/ui.
 *
 * Owns the canonical responsive grid rhythm, a `popLayout` reflow, a per-row
 * staggered spring entrance, a crossfade from the loading skeletons to the real
 * cards, and count-matched skeletons so the layout never collapses while a
 * result set loads. It is the engine the existing type-specific grids
 * (`ExperienceCardGrid` / `StudioCardGrid` / `CollectionCardGrid`) will fold
 * into; it is shipped additively here (it does not touch those grids, so their
 * published API is unchanged) and the consolidation is a follow-on.
 *
 * Behaviour:
 * - **Per-row staggered entrance.** Cells fan in by column *within each row*
 *   (`delay: (i % cols) * 0.04`), so the whole grid settles in roughly one beat
 *   instead of rippling diagonally from a single global ramp. Each cell lands
 *   with a short scale+fade and a layout spring (stiffness 260 / damping 30).
 *   Motion technique referenced from React Bits Pro `ecommerce-6` (per-row
 *   modulo stagger + flat spring transition); reimplemented, not copied. The
 *   constants also match the in-house `card-in` token in `tailwind.preset.ts`.
 * - **Crossfade.** One `AnimatePresence` wraps BOTH the skeleton set and the
 *   live set, so on a loading -> content swap the skeletons exit as the cards
 *   enter, instead of an abrupt branch switch. The skeleton cells share the
 *   same per-row stagger so the loading and loaded states speak one language.
 * - **Count-matched skeletons.** `skeletonCount` defaults to the last known item
 *   count, so the loading footprint matches the results that are coming back.
 *   The default skeleton is a composed card silhouette (image block + two text
 *   lines), overridable via `skeleton`, so the crossfade reads as a card
 *   filling in rather than a bare box morphing.
 * - **Filter reflow.** Pass `transitionKey` (e.g. the active filter signature);
 *   changing it replays the staggered entrance, so a filter change visibly
 *   re-fills rather than only diffing.
 * - **Reduced motion.** The layout spring + stagger collapse to an instant,
 *   opacity-only render under `prefers-reduced-motion`.
 *
 * @example
 *   <CardGrid
 *     items={studios}
 *     getKey={(s) => s.id}
 *     renderItem={(s) => <StudioCard {...s} />}
 *     loading={isLoading}
 *     transitionKey={`${role}:${sort}`}
 *   />
 */
const GRID_COLUMNS = {
  "2": "grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6",
  "3": "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6",
  "4": "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6",
} as const;

// Max column count per `columns` prop — the modulo base for the per-row stagger.
// KNOWN APPROXIMATION: the grid steps responsively (1 -> 2 -> 3 -> 4), so the
// actual rendered column count is only known by measuring the DOM. We key the
// modulo off the prop's *widest* breakpoint, the same simplification the
// `ecommerce-6` reference makes by hardcoding `% 4`. At narrow viewports the
// fan-in degrades toward linear, which is acceptable for a polish-tier entrance.
// Do NOT "fix" this into a ResizeObserver — not worth the complexity here.
const STAGGER_COLUMNS = { "2": 2, "3": 3, "4": 4 } as const;

// On-brand entrance constants. The layout spring (260/30) + 0.04 per-row
// stagger + 0.35s settle match React Bits Pro `ecommerce-6` AND the in-house
// `card-in` token (320ms out-soft translateY). Reimplemented from technique,
// not copied. These are motion parameters, not licensed source.
const ENTRANCE_DURATION = 0.35;
const STAGGER_STEP = 0.04;
const LAYOUT_SPRING = { type: "spring", stiffness: 260, damping: 30 } as const;

export interface CardGridProps<T> {
  items: T[];
  /** Render one card for an item. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Stable key per item (drives the reflow diff). */
  getKey: (item: T, index: number) => React.Key;
  /** Responsive column rhythm. Matches the existing card grids. */
  columns?: keyof typeof GRID_COLUMNS;
  /** Animate the reflow + entrance (default true). */
  animate?: boolean;
  /** Show skeletons instead of items. */
  loading?: boolean;
  /**
   * Rendered when the result set is legitimately zero-length. Omitted by
   * default, because "no cards" is a blank region in plenty of layouts and a
   * grid should not invent copy for it — pass an `<EmptyState>` when the
   * surface owns that answer.
   *
   * `loading` wins: an empty array mid-fetch means "not yet", not "nothing",
   * and showing "you have no studios" over a request still in flight is the
   * single most common way this state gets told wrong.
   */
  empty?: React.ReactNode;
  /** Skeleton rendered per slot while loading. Defaults to a card Skeleton. */
  skeleton?: React.ReactNode;
  /** Skeleton count. Defaults to the last known item count (clamped), else 6. */
  skeletonCount?: number;
  /** Changing this replays the staggered fill-from-empty entrance. */
  transitionKey?: React.Key;
  className?: string;
}

function CardGridInner<T>(
  {
    items,
    renderItem,
    getKey,
    columns = "3",
    animate = true,
    loading = false,
    empty,
    skeleton,
    skeletonCount,
    transitionKey,
    className,
  }: CardGridProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const reduced = useReducedMotion();
  // reduced === true -> no movement; null (SSR/unknown) -> allow, matching the hook.
  const moves = animate && reduced !== true;
  const cols = STAGGER_COLUMNS[columns];

  // Remember the last non-empty result count so the loading footprint matches
  // the incoming results.
  const lastCountRef = React.useRef(6);
  if (!loading && items.length > 0) lastCountRef.current = Math.min(items.length, 12);
  const count = Math.max(1, Math.min(skeletonCount ?? lastCountRef.current, 24));

  // See `empty` — loading takes precedence over emptiness.
  const isEmpty = !loading && items.length === 0;

  // Default skeleton is a composed card silhouette (image block + two text
  // lines) so the loading footprint matches a real card and the crossfade
  // reads as a card filling in, not a bare rect morphing. Overridable via the
  // `skeleton` prop. Uses the preset `shimmer` animation (reduced-motion-safe).
  const skeletonNode = skeleton ?? (
    <div className="space-y-3">
      <Skeleton variant="card" animation="shimmer" className="aspect-[4/3] w-full" />
      <Skeleton variant="text" animation="shimmer" className="w-3/4" />
      <Skeleton variant="text" animation="shimmer" className="w-1/2" />
    </div>
  );

  // Per-row fan-in: each row's cells stagger by column, then the next row
  // restarts its own fan, so the grid lands in one beat. Flat transition (a
  // single duration + delay, no per-key sub-objects) so the delay actually
  // applies — splitting opacity/y into their own transitions silently drops
  // the delay in motion/react.
  const cellTransition = (i: number) =>
    moves
      ? { layout: LAYOUT_SPRING, duration: ENTRANCE_DURATION, delay: (i % cols) * STAGGER_STEP }
      : { duration: 0 };

  return (
    <motion.div
      ref={ref}
      // TODO(aisquare-ui#167): the ecommerce-6 in-place FLIP reflow is not
      // animating yet, so the `layout` springs are commented out here AND on the
      // items below (the release ships a clean static grid with entrance/exit
      // only). A follow-up PR should UNCOMMENT `layout={moves ? true : undefined}`
      // on this container + the items once the FLIP is working.
      // layout={moves ? true : undefined}
      key={transitionKey}
      // No column track when empty: the grid's job is to lay out cards, and an
      // empty slot rendered into a cell would be pinned to one third of the row.
      className={cn(!isEmpty && GRID_COLUMNS[columns], className)}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {loading
          ? Array.from({ length: count }).map((_, i) => (
              <motion.div
                key={`skeleton-${i}`}
                initial={moves ? { opacity: 0, y: 10 } : false}
                animate={{ opacity: 1, y: 0 }}
                exit={moves ? { opacity: 0, y: -6 } : undefined}
                transition={cellTransition(i)}
              >
                {skeletonNode}
              </motion.div>
            ))
          : isEmpty
            ? empty && (
                <motion.div
                  key="empty"
                  initial={moves ? { opacity: 0, y: 10 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  exit={moves ? { opacity: 0, y: -6 } : undefined}
                  transition={cellTransition(0)}
                >
                  {empty}
                </motion.div>
              )
            : items.map((item, i) => (
                <motion.div
                  // STABLE key (not folded with transitionKey): so a filter change
                  // PRESERVES surviving items' identity and animates their layout
                  // (FLIP) into the freed cells, while removed items exit in place,
                  // instead of remounting + replaying the whole entrance (ripple).
                  // transitionKey is now a whole-grid remount escape hatch (on the
                  // container) for a genuinely new dataset, not the filter mechanism.
                  key={getKey(item, i)}
                  // TODO(aisquare-ui#167): FLIP layout disabled (see container note); uncomment to re-enable.
                  // layout={moves ? true : undefined}
                  initial={moves ? { opacity: 0, y: 10 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  exit={moves ? { opacity: 0, y: -6 } : undefined}
                  transition={cellTransition(i)}
                >
                  {renderItem(item, i)}
                </motion.div>
              ))}
      </AnimatePresence>
    </motion.div>
  );
}

// forwardRef erases generics, so re-assert the generic call signature via a cast
// (a standard pattern for a generic forwardRef component).
export const CardGrid = React.forwardRef(CardGridInner) as <T>(
  props: CardGridProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
